import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Constants } from '../lib/const/constants';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchTransactionById } from '../utils/CommonFunctions';
import { encryptPayload } from '../utils/encryption';

const tagStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 8px',
  background: '#F6F6FB',
  borderRadius: 4,
  whiteSpace: 'nowrap',
  fontSize: '12px',
  fontWeight: '400',
  color: '#676F7D',
  lineHeight: '16.5px',
};

function TransactionMeta({ transactionId }) {
  const [txn, setTxn]         = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchTransactionById(transactionId);
      if (!cancelled) {
        setTxn(data || null);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [transactionId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        {[80, 60, 70, 50].map((w, i) => (
          <div key={i} style={{ width: w, height: 24, background: '#F6F6FB', borderRadius: 4 }} />
        ))}
      </div>
    );
  }

  if (!txn) return null;

  const items = [
    { label: 'Txn ID', value: `#${transactionId}` },
    txn.name && {                         
      label: 'Beneficiary',
      value: txn.name,
      maxWidth: '220px',
      title: txn.name,
    },
    txn.status && { label: 'Status', value: txn.status },
    txn.amount && {
      label: 'Amount',
      value: `${txn.currency} ${isNaN(Number(txn.amount)) ? txn.amount : Number(txn.amount).toFixed(2)}`,
    },
    txn.type && { label: 'Type', value: txn.type },
  ].filter(Boolean);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
      {items.map((item) => (
        <span
          key={item.label}
          style={tagStyle}
          title={item.title || undefined}
        >
          {item.label}:{' '}
          <strong
            style={{
              fontWeight: 500,
              color: '#1D2338',
              fontSize: '12px',
              lineHeight: '16.5px',
              maxWidth: item.maxWidth || undefined,
              overflow: item.maxWidth ? 'hidden' : undefined,
              textOverflow: item.maxWidth ? 'ellipsis' : undefined,
              display: item.maxWidth ? 'inline-block' : undefined,
              verticalAlign: item.maxWidth ? 'bottom' : undefined,
            }}
          >
            {item.value}
          </strong>
        </span>
      ))}
    </div>
  );
}

TransactionMeta.propTypes = {
  transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// ─────────────────────────────────────────────────────────────────────────────

function DashboardDocuments({ documents = [], loading = false }) {
  const navigate = useNavigate();

  const [files,         setFiles]         = useState({});
  const [uploading,     setUploading]     = useState(false);
  const [showUploadFor, setShowUploadFor] = useState(null);

  const statusConfig = {
    Required:         { bg: '#FEF2F2', color: '#DC2626', label: 'Required' },
    'Pending Review': { bg: '#FFF7ED', color: '#EA580C', label: 'Pending Review' },
    Approved:         { bg: '#dcfce7', color: '#166534', label: 'Approved' },
    Rejected:         { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
  };

  const getStatus = (doc) => {
    if (doc.status) return doc.status;
    if (doc.uploaded_document && doc.uploaded_document !== '') return 'Pending Review';
    return 'Required';
  };

  const attentionCount = documents.filter(
    (d) => !d.uploaded_document || d.uploaded_document === '',
  ).length;

  const handleFileChange = (event, docIndex) => {
    setFiles((prev) => ({ ...prev, [docIndex]: event.target.files }));
  };

  const handleUpload = async (doc, docIndex) => {
    const fileList = files[docIndex];
    if (!fileList || fileList.length === 0) {
      toast.error('Please select a file first.');
      return;
    }
    const token = sessionStorage.getItem('login_token');
    setUploading(true);
    Array.from(fileList).forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const extension = file.name.split('.');
        const encryptedPayload = await encryptPayload({
              transaction_id: doc.transaction_id,
              document_id:    doc.document_id,
              document:       reader.result,
              extension:      extension[extension.length - 1],
              platform: 'web',
              extraParamValidation: 'trustedfromweb'
            });
        try {
          await axios.post(
            Constants.BASE_URL + 'API-FX-194-UPLOAD-TRANSACTION-DOCUMENTS',
            encryptedPayload,
            {
              headers: {
                Authorization: 'Bearer ' + JSON.parse(token),
                fx_key: Constants.SUBSCRIPTION_KEY,
              },
            },
          );
          toast.success('Uploaded successfully.');
          setUploading(false);
          setShowUploadFor(null);
          setFiles({});
        } catch (err) {
          toast.error('Something went wrong. Please try again later.');
          console.log('upload error', err);
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const SkeletonRow = () => (
    <div style={{ padding: '18px 24px', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ width: 160, height: 14, background: '#e5e7eb', borderRadius: 6, marginBottom: 8 }} />
          <div style={{ width: 240, height: 11, background: '#f3f4f6', borderRadius: 6, marginBottom: 6 }} />
        </div>
        <div style={{ width: 72, height: 32, background: '#e5e7eb', borderRadius: 8, flexShrink: 0, marginLeft: 16 }} />
      </div>
    </div>
  );

  return (
    <>
      <ToastContainer />
      <div
        style={{
          borderRadius: 16,
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minWidth: 0,
        }}
      >
        {/* Header */}
        <div style={{ background: '#676F7D', padding: '18px 24px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#ffffff' }}>Documents</span>
          </div>
          <p style={{ fontSize: 13, fontWeight: 500, color: '#E6E5E5' }}>
            {loading
              ? 'Loading your documents…'
              : documents.length === 0
              ? 'No documents required at this time.'
              : `You have ${attentionCount} document(s) that need your attention.`}
          </p>
        </div>

        {/* Body */}
        <div style={{ background: '#ffffff', overflowY: 'auto', maxHeight: 360 }}>
          {loading ? (
            <><SkeletonRow /><SkeletonRow /></>
          ) : documents.length === 0 ? (
            <div style={{ padding: '28px 24px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              All documents are up to date.
            </div>
          ) : (
            documents.map((doc, i) => {
              const status     = getStatus(doc);
              const sc         = statusConfig[status] || { bg: '#f3f4f6', color: '#6b7280', label: status };
              const isRequired = !doc.uploaded_document || doc.uploaded_document === '';
              const isExpanded = showUploadFor === doc.document_id;

              return (
                <div
                  key={doc.document_id || i}
                  style={{ padding: '18px 24px', borderBottom: i < documents.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                >
                  {/* Row: title + badge + button */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 16, fontWeight: 600, color: '#101828' }}>
                          {doc.document || 'Document'}
                        </span>
                        <span style={{ background: sc.bg, color: sc.color, fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                          {sc.label}
                        </span>
                      </div>

                      {doc.type && (
                        <p style={{ fontSize: 13, color: '#4A5565', lineHeight: '18px', marginBottom: 2 }}>
                          {doc.type}
                        </p>
                      )}

                      {/* ── Inline transaction meta ── */}
                      {doc.transaction_id && (
                        <TransactionMeta transactionId={doc.transaction_id} />
                      )}
                    </div>

                    {isRequired ? (
                      <button
                        onClick={() => setShowUploadFor(isExpanded ? null : doc.document_id)}
                        className="w-[88px] h-[41px] px-[18px] py-[8px] rounded-[10px] text-white text-[13px] font-semibold whitespace-nowrap flex-shrink-0 cursor-pointer bg-[#D26521] shadow-[0px_2px_4px_rgba(255,107,53,0.3)] hover:bg-[#b8571c]"
                      >
                        {isExpanded ? 'Cancel' : 'Upload'}
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate('/contactsupport')}
                        style={{ background: 'none', border: '1.5px solid #1d4ed8', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, color: '#1d4ed8', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        View Status
                      </button>
                    )}
                  </div>

                  {/* Upload panel */}
                  {isExpanded && isRequired && (
                    <div style={{ marginTop: 12, padding: '14px 16px', border: '1.5px dashed #d1d5db', borderRadius: 10, background: '#f9fafb' }}>
                      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                        Upload <strong>{doc.document}</strong> for Transaction #{doc.transaction_id}
                      </p>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileChange(e, i)} style={{ fontSize: 12, marginBottom: 10, display: 'block' }} />
                      {files[i] && files[i].length > 0 && (
                        <p style={{ fontSize: 11, color: '#16a34a', marginBottom: 8 }}>Selected: {files[i][0].name}</p>
                      )}
                      <button
                        onClick={() => handleUpload(doc, i)}
                        disabled={uploading}
                        style={{ background: uploading ? '#9ca3af' : '#EB640A', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 20px', fontSize: 13, fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer' }}
                      >
                        {uploading ? 'Uploading…' : 'Submit'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {!loading && (
          <div style={{ padding: '14px 24px', textAlign: 'right', borderTop: '1px solid #f0f0f0', background: '#ffffff', flexShrink: 0 }}>
            <button
              onClick={() => navigate('/transactions')}
              style={{ background: 'none', border: 'none', color: '#306ACE', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0 }}
            >
              View All Documents →
            </button>
          </div>
        )}
      </div>
    </>
  );
}

DashboardDocuments.propTypes = {
  documents: PropTypes.array,
  loading:   PropTypes.bool,
};

export default DashboardDocuments;